import React, { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { Icon, IconTypesKeys } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import {
  DialogContent,
  ProjectMetaBlock,
  RestrictedWidthContainer,
  SourceDescription,
} from "../../../DialogComponents/styled";

import { LinearProgress, LoadingDescription } from "../../../BulkCopy/styled";
import { ResourceMeta } from "../../../DialogComponents/ResourceMeta/ResourceMeta";
import { DataField, FieldValue } from "../../../styled";
import { DialogActions } from "../../../DialogComponents/DialogActions";
import { CopyLayerOptions } from "./CopyLayerOptions";

import { FILTERED_OPTIONS } from "../../constants";

import { formatNumber } from "../../../../../utils/formatNumber";
import { filteredFeaturesCount } from "../../../../../resources/layers";
import { getLayerInfo } from "../../../../../evergis/core/layer/getLayerInfo";
import { getUsername } from "../../../../../ducks/user";
import { useCheckLimits } from "../../../../../hooks/useCheckLimits";
import { useCopyLayerOptions } from "./hooks/useCopyLayerOptions";
import { useCopyLayerJob } from "./hooks/useCopyLayerJob";
import { useRowsCount } from "./hooks/useRowsCount";
import { useDialogInfo } from "../../../../../hooks/useDialogs";
import { useTableSelection } from "../../../../../hooks/table/useTableSelection";
import { useDefaultScaleRange } from "../../../../../hooks/style/useDefaultScaleRange";

import { CopyToNewProps, LayerCopyOptionsDeps } from "./types";
import { TableInfo } from "../../../../../interfaces/resourceInfo";

// eslint-disable-next-line max-lines-per-function
export const CopyToNew: FC<CopyToNewProps> = ({ layerInfo, loading, shadows, setScrollContainer }) => {
  const { t } = useTranslation("dialogs");
  const defaultScaleRange = useDefaultScaleRange();
  const currentLayer = useMemo(() => getLayerInfo(layerInfo && layerInfo.layers[0], defaultScaleRange), [layerInfo]);
  const { data: filteredCount } = filteredFeaturesCount.use({ input: currentLayer.name, waitInput: true });
  const isFiltered: boolean = currentLayer && !!currentLayer.condition;
  const deps: LayerCopyOptionsDeps = layerInfo && layerInfo.deps;
  const fullTableInfo: TableInfo = (deps && deps.tables && deps.tables[0]) || {};
  const { alias: tableAlias, name: tableName, owner: tableOwner, rowCount: tableObjectsCount } = fullTableInfo;
  const currentUser: string = useSelector(getUsername);
  const { limits } = useCheckLimits();
  const [options, changeOptions] = useCopyLayerOptions(isFiltered, fullTableInfo, limits);
  const shouldCopyTable = options === FILTERED_OPTIONS.FILTERED_ONLY || options === FILTERED_OPTIONS.FULL_DATA;
  const shouldFilter = options === FILTERED_OPTIONS.FILTERED_ONLY;
  const { meta, updateMeta, onApply, waiting } = useCopyLayerJob(layerInfo, shouldCopyTable, shouldFilter);
  const { options: dialogOptions } = useDialogInfo();
  const fromLayerTable = useMemo(() => dialogOptions?.fromTable, [dialogOptions]);
  const [tableSelection] = useTableSelection();
  const rowCount = useRowsCount(currentLayer);

  return (
    <>
      <DialogContent ref={setScrollContainer}>
        <RestrictedWidthContainer>
          {loading ? (
            <>
              <LoadingDescription>{t("copyResource.gettingLayerData")}</LoadingDescription>
              <LinearProgress />
            </>
          ) : (
            <>
              <ProjectMetaBlock>
                <ResourceMeta meta={meta} updateMeta={updateMeta}>
                  {currentLayer.type !== "ProxyService" && (
                    <>
                      <DataField column>
                        <FieldValue
                          field={t("copyResource.table")}
                          value={
                            <>
                              {tableOwner && tableOwner !== currentUser && (
                                <Icon
                                  kind={"warning"}
                                  style={{ marginRight: "0.2rem" }}
                                  error
                                  title={`${t("copyResource.tableOwner")} ${tableOwner}`}
                                />
                              )}
                              {currentLayer && (
                                <Icon
                                  kind={`geom_${currentLayer.geometryType}` as IconTypesKeys}
                                  style={{ marginRight: "0.5rem" }}
                                  error={!tableName}
                                />
                              )}
                              <SourceDescription error={!tableName} noPadding>
                                {tableAlias || tableName || t("copyResource.notFound")}
                                {fromLayerTable && tableSelection?.[currentLayer.name] && ` ${t("bulkCopy.copy")}`}
                              </SourceDescription>
                            </>
                          }
                        />
                      </DataField>
                      <DataField column>
                        <FieldValue
                          field={t("copyResource.totalFeatures")}
                          value={
                            (fromLayerTable && tableSelection?.[currentLayer.name]) ||
                            (!fromLayerTable && isFiltered && tableName)
                              ? `${formatNumber(rowCount)} ${t("copyResource.of")} ${formatNumber(tableObjectsCount)}`
                              : formatNumber(tableObjectsCount)
                          }
                        />
                      </DataField>
                    </>
                  )}
                </ResourceMeta>
              </ProjectMetaBlock>
              {!(fromLayerTable && tableSelection?.[currentLayer.name]) &&
                !["ProxyService", "PostgresTileLayerService"].includes(currentLayer.type) && (
                  <CopyLayerOptions
                    deps={deps}
                    options={options}
                    changeOptions={changeOptions}
                    isFiltered={isFiltered}
                    filteredCount={filteredCount}
                    tableInfo={fullTableInfo}
                    limits={limits}
                  />
                )}
            </>
          )}
        </RestrictedWidthContainer>
      </DialogContent>
      <DialogActions
        displayShadow={shadows.bottom}
        applyDisabled={!meta.alias || loading || (currentLayer.type !== "ProxyService" && !tableName)}
        onApply={onApply}
        isWaiting={waiting}
      />
    </>
  );
};
